<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Appointment Type webpage -->
<template>
  <div class="appointment-type page">
    <template v-if="appointmentType">
      <div class="page-heading">
        <h1>{{ appointmentType.name }}</h1>
      </div>

      <div class="page-content">
        <!-- General Information -->
        <section class="page-section general">
          <h4>General</h4>

          <label class="av-label">Description</label>
          {{ appointmentType.description }}

          <label class="av-label">Duration</label>
          {{ appointmentType.duration }} Minutes ( {{ formatDuration(appointmentType.duration) }} )

          <label class="av-label">Visibility</label>
          {{ appointmentType.public ? 'Public' : 'Private' }}

          <br>
          <a class="shaded" @click="shouldShowEditForm = true"><i class="fal fa-edit"></i> Edit</a>
        </section>

        <!-- General Information Form -->
        <appointment-type-form
          :visible="shouldShowEditForm"
          :appointmentType="appointmentType"
          :editing="true"
          @updated="handleAppointmentTypeUpdate"
          @close="shouldShowEditForm = false"
        ></appointment-type-form>

        <!-- Times Offered -->
        <section v-if="!appointmentType.isTask" class="page-section offerings">
          <h4>Times Offered</h4>

          <ul>
            <li v-for="offering of appointmentType.offerings" :key="offering.id">
              {{ formatOffering(offering.hour) }}
              <i class="fal fa-times-circle" @click="removeOffering(offering)"></i>
            </li>
          </ul>

          <div class="warning" v-if="appointmentType.offerings.length === 0">There are currently no offerings.</div>

          <br>
          <a class="shaded" @click="shouldShowTimeForm = true"><i class="fal fa-plus-circle"></i> Add</a>
        </section>

        <!-- Intake Form -->
        <section class="page-section intake-form">
          <h4>Intake Form</h4>

          <template v-if="appointmentType.currentForm">
            <label class="av-label">Form Description</label>
            <p v-if="appointmentType.currentForm.description !== '' && appointmentType.currentForm.description">{{ appointmentType.currentForm.description }}</p>
            <p v-else>No Description</p>
            <br><br>
            <div class="intake-form-field-display" v-for="field of appointmentType.currentForm.inputs" :key="field.id">
              <span class="type">
                <i class="fal fa-text" v-if="field.type === 'text'"></i>
                <i class="fal fa-envelope" v-if="field.type === 'email'"></i>
                <i class="fal fa-phone" v-if="field.type === 'phone'"></i>
                <i class="fal fa-circle" v-if="field.type === 'radio'"></i>
                <i class="fal fa-check" v-if="field.type === 'checkbox'"></i>
                <i class="fal fa-check-double" v-if="field.type === 'checkbox group'"></i>
                <i class="fal fa-comment-alt" v-if="field.type === 'textbox'"></i>
                {{ field.type }}
              </span>
              <h5>{{ field.label }}</h5>
              <span>{{ field.required ? 'Required' : 'Optional' }}</span>
            </div>

            <div class="warning" v-if="appointmentType.currentForm.inputs.length === 0">There are currently no custom fields.</div>
          </template>

          <br>
          <a class="shaded" @click="shouldShowIntakeFormEditor = true"><i class="fal fa-edit"></i> Edit</a>
        </section>

        <!-- Confirmation Message -->
        <section class="page-section confirmation-message">
          <h4>Confirmation Message</h4>

          <div class="ql-editor" v-html="appointmentType.emailConfirmationMessage"></div>
          <br>
          <a class="shaded" @click="shouldShowConfirmationMessageForm = true"><i class="fal fa-edit"></i> Edit</a>
        </section>
      </div>

      <!-- Confirmation Message Form -->
      <av-modal
        class="message-editor"
        :visible="shouldShowConfirmationMessageForm"
        @close="shouldShowConfirmationMessageForm = false"
      >
        <div id="editor"></div>
        <div class="modal-actions">
          <av-button class="error hollow" @click="shouldShowConfirmationMessageForm = false">Cancel</av-button>
          <av-button @click="saveConfirmationMessage">Save</av-button>
        </div>
      </av-modal>

      <!-- Intake Form Editor -->
      <intake-form-editor
        :visible="shouldShowIntakeFormEditor"
        :appointmentTypeId="appointmentType.id"
        :intakeForm="appointmentType.currentForm"
        @close="shouldShowIntakeFormEditor = false"
        @form-updated="updateIntakeForm($event)"
      ></intake-form-editor>

      <!-- Add new time form -->
      <av-modal :visible="shouldShowTimeForm" @close="shouldShowTimeForm = false">
        <div class="modal-header">Add New Time</div>
        <div class="modal-content">
          <input class="av-input" placeholder="Time (ex. 10:30am)" v-model="editedtime" />
        </div>
        <div class="modal-actions">
          <av-button class="error hollow" @click="shouldShowTimeForm = false">Cancel</av-button>
          <av-button @click="addOffering()">Add</av-button>
        </div>
      </av-modal>

    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { format } from 'date-fns'
import timeService from '../common/timeService'
import AppointmentTypeService from '../common/appointmentTypeService'
import AppointmentTypeOffering from '../models/appointmentTypeOffering'
import { AppointmentType, AppointmentTypeDetail } from '../models/appointmentType'
import IntakeFormEditor from '../components/IntakeFormEditor.vue'
import AppointmentTypeForm from '../components/AppointmentTypeForm.vue'
import Quill from '../common/quill'
// import Quill from 'quill/core'

export default defineComponent({
  name: 'AppointmentType',

  data () {
    return {
      /** The AppointmentType to view and edit */
      appointmentType: null as unknown as AppointmentTypeDetail,

      /** New offering time */
      editedtime: '',

      /** Form containing edited general informaiton */
      editForm: null as unknown as AppointmentType,

      /** The Appointment type's confirmation message */
      confirmationMessage: '',

      quill: null as any,

      /** Indicator for showing confirmation message form */
      shouldShowConfirmationMessageForm: false,

      /** Indicator to show form for general info */
      shouldShowEditForm: false,

      /** Indicator to show the intake form editor */
      shouldShowIntakeFormEditor: false,

      /** Indicator to show offering form */
      shouldShowTimeForm: false
    }
  },

  watch: {
    $route: {
      handler (route) {
        AppointmentTypeService.get(route.params.id).then(res => {
          this.appointmentType = res.data
        })
      },
      immediate: true
    },

    /** Indicate if should show Confirmation Message Form */
    shouldShowConfirmationMessageForm (val) {
      if (val) {
        setTimeout(() => {
          this.quill = new Quill('#editor', {
            modules: {
              toolbar: [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link']
              ]
            },
            theme: 'snow'
          }) as Quill

          this.quill.root.innerHTML = this.appointmentType.emailConfirmationMessage
        }, 0)
      }
    }
  },

  computed: {
    /** Determines if the new time is valid */
    isValidTime (): boolean {
      return timeService.isValidTimeString(this.editedtime)
    }
  },

  methods: {
    /**
     * Adds a new offering
     */
    addOffering () {
      const time = timeService.convertTimeFromString(this.editedtime)
      if (time == null) {
        return
      }

      AppointmentTypeService.createOffering(this.appointmentType.id, time).then(res => {
        this.appointmentType.offerings.push(res.data)
        this.editedtime = ''
        this.shouldShowTimeForm = false
      })
    },

    /**
     * Updates the appointment types's general info
     */
    handleAppointmentTypeUpdate (appointmentType: AppointmentType) {
      Object.assign(this.appointmentType, appointmentType)
      this.shouldShowEditForm = false
    },

    /**
     * Formats a date time as a string
     */
    format (date: Date): string {
      return format(date, 'MMM do hh:mma (zzz)')
    },

    /**
     * Formats a time represented as a number as a string
     * @param timeNum: The time represented as the number of minutes since the start of day
     */
    formatOffering (timeNum: number): string | null {
      return timeService.convertTimeToString(timeNum)
    },

    formatDuration (timeNum: number): string {
      return timeService.convertDurationToString(timeNum)
    },

    /**
     * Removes the given offering
     * @param: The offering
     */
    removeOffering (offering: AppointmentTypeOffering) {
      if (window.confirm('Are you sure?')) {
        AppointmentTypeService.removeOffering(offering.id).then(() => {
          const ind = this.appointmentType.offerings.findIndex(at => at.id === offering.id)
          this.appointmentType.offerings.splice(ind, 1)
        })
      }
    },

    /**
     * save confirmation message
     */
    saveConfirmationMessage () {
      const message = this.quill.root.innerHTML
      AppointmentTypeService.updateConfirmationMessage(this.appointmentType.id, message).then(res => {
        this.appointmentType.emailConfirmationMessage = message
        this.shouldShowConfirmationMessageForm = false
      })
    },

    /**
     * Updates the intake form
     * @param newForm: The updated intake form
     */
    updateIntakeForm (newForm) {
      this.appointmentType.currentForm = newForm
      this.shouldShowIntakeFormEditor = false
    }
  },

  components: {
    IntakeFormEditor,
    AppointmentTypeForm
  }
})
</script>
